 import React, { useState, useEffect } from "react";
 import { useOutletContext, useNavigate } from "react-router-dom";
 import { NursingHome } from "@/hooks/use-nursing-home";
 import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
 import { Button } from "@/components/ui/button";
 import { Badge } from "@/components/ui/badge";
 import { supabase } from "@/integrations/supabase/client";
 import { useSubscription } from "@/hooks/use-subscription";
 import { Loader2, Users, Lock, Mail, Phone, MapPin, Calendar, Crown, Eye, EyeOff } from "lucide-react";
 import { format } from "date-fns";
 
 type ContextType = { nursingHome: NursingHome };
 
 interface Lead {
   id: string;
   first_name: string;
   last_name: string;
   email: string;
   phone: string | null;
   preferred_location: string[];
   specific_nursing_home: string | null;
   status: string;
   created_at: string;
   timeframe: string | null;
   care_needs: string[] | null;
 }
 
 export default function LeadsPage() {
   const { nursingHome } = useOutletContext<ContextType>();
   const navigate = useNavigate();
   const { subscribed, plan_tier, loading: subscriptionLoading } = useSubscription();
   const [leads, setLeads] = useState<Lead[]>([]);
   const [leadCount, setLeadCount] = useState(0);
   const [loading, setLoading] = useState(true);
 
   useEffect(() => {
     fetchLeads();
   }, [nursingHome.county]);
 
   const fetchLeads = async () => {
     try {
       // Get leads count for this nursing home's county
       const countyName = nursingHome.county?.toLowerCase();
       
       // Count leads that match this nursing home
       const { count: directCount } = await supabase
         .from("nursing_home_leads")
         .select("*", { count: "exact", head: true })
         .eq("nursing_home_id", nursingHome.nh_slug);
 
       // Count waitlist submissions for this county
       const { count: waitlistCount } = await supabase
         .from("waitlist_submissions")
         .select("*", { count: "exact", head: true })
         .contains("preferred_location", [countyName]);
 
       const totalCount = (directCount || 0) + (waitlistCount || 0);
       setLeadCount(totalCount);
 
       // Only fetch full lead details if subscribed
       if (subscribed) {
         // Fetch direct leads for this nursing home
         const { data: directLeads } = await supabase
           .from("nursing_home_leads")
           .select("*")
           .eq("nursing_home_id", nursingHome.nh_slug)
           .order("created_at", { ascending: false });
 
         // Fetch waitlist leads for this county
         const { data: waitlistLeads } = await supabase
           .from("waitlist_submissions")
           .select("*")
           .contains("preferred_location", [countyName])
           .order("created_at", { ascending: false })
           .limit(50);
 
         // Combine and map to common format
         const combinedLeads: Lead[] = (waitlistLeads || []).map(w => ({
           id: w.id,
           first_name: w.first_name,
           last_name: w.last_name,
           email: w.email,
           phone: w.phone,
           preferred_location: w.preferred_location || [],
           specific_nursing_home: w.specific_nursing_home,
           status: w.status || "pending",
           created_at: w.created_at,
           timeframe: w.timeframe,
           care_needs: w.care_needs,
         }));
 
         setLeads(combinedLeads);
       }
     } catch (error) {
       console.error("Error fetching leads:", error);
     } finally {
       setLoading(false);
     }
   };
 
   const maskEmail = (email: string) => {
     const [name, domain] = email.split("@");
     return `${name.slice(0, 2)}${"*".repeat(name.length - 2)}@${domain}`;
   };
 
   const maskPhone = (phone: string) => {
     return phone.slice(0, 4) + "*".repeat(phone.length - 4);
   };
 
   if (loading || subscriptionLoading) {
     return (
       <div className="flex items-center justify-center py-12">
         <Loader2 className="h-8 w-8 animate-spin text-primary" />
       </div>
     );
   }
 
   return (
     <div className="space-y-6">
       <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
         <div>
           <h1 className="text-2xl font-bold tracking-tight">Lead Management</h1>
           <p className="text-muted-foreground">
             View and manage potential residents interested in {nursingHome.center_name}
           </p>
         </div>
       </div>
 
       {/* Lead Summary Card */}
       <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
         <CardContent className="p-6">
           <div className="flex items-center justify-between">
             <div className="flex items-center gap-4">
               <div className="rounded-full p-3 bg-primary/10">
                 <Users className="h-6 w-6 text-primary" />
               </div>
               <div>
                 <p className="text-sm text-muted-foreground">Total Leads Available</p>
                 <p className="text-3xl font-bold">{leadCount}</p>
               </div>
             </div>
             {!subscribed && leadCount > 0 && (
               <Badge variant="secondary" className="text-sm">
                 <Lock className="h-3 w-3 mr-1" />
                 Premium Required
               </Badge>
             )}
           </div>
         </CardContent>
       </Card>
 
       {/* Paywall for non-subscribers */}
       {!subscribed && leadCount > 0 ? (
         <Card className="border-2 border-dashed border-primary/30">
           <CardHeader className="text-center pb-2">
             <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
               <Crown className="h-8 w-8 text-primary" />
             </div>
             <CardTitle className="text-xl">Unlock {leadCount} Potential Residents</CardTitle>
             <CardDescription className="text-base max-w-md mx-auto">
               Subscribe to view contact details and connect with families actively searching for care in your area.
             </CardDescription>
           </CardHeader>
           <CardContent className="text-center space-y-6">
             {/* Preview of blurred leads */}
             <div className="space-y-3 opacity-60">
               {[1, 2, 3].map((i) => (
                 <div key={i} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg blur-sm">
                   <div className="flex items-center gap-3">
                     <div className="h-10 w-10 rounded-full bg-muted" />
                     <div>
                       <div className="h-4 w-32 bg-muted rounded" />
                       <div className="h-3 w-24 bg-muted rounded mt-1" />
                     </div>
                   </div>
                   <div className="flex gap-2">
                     <div className="h-8 w-20 bg-muted rounded" />
                     <div className="h-8 w-20 bg-muted rounded" />
                   </div>
                 </div>
               ))}
             </div>
 
             <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
               <Button size="lg" onClick={() => navigate("/owner/subscription")}>
                 <Crown className="mr-2 h-4 w-4" />
                 View Subscription Plans
               </Button>
               <Button size="lg" variant="outline" onClick={() => navigate("/owner/subscription")}>
                 Starting at €29/month
               </Button>
             </div>
 
             <p className="text-sm text-muted-foreground">
               Each lead represents a family actively searching for nursing home care.
               <br />
               Don't miss out on connecting with potential residents.
             </p>
           </CardContent>
         </Card>
       ) : subscribed && leads.length > 0 ? (
         /* Full lead list for subscribers */
         <div className="space-y-4">
           <div className="flex items-center justify-between">
             <h2 className="text-lg font-semibold">Recent Leads</h2>
             <Badge variant="outline" className="text-primary">
               <Eye className="h-3 w-3 mr-1" />
               {plan_tier?.charAt(0).toUpperCase()}{plan_tier?.slice(1)} Access
             </Badge>
           </div>
           
           <div className="grid gap-4">
             {leads.map((lead) => (
               <Card key={lead.id} className="hover:shadow-md transition-shadow">
                 <CardContent className="p-4">
                   <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                     <div className="flex items-start gap-4">
                       <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                         <span className="text-lg font-semibold text-primary">
                           {lead.first_name.charAt(0)}{lead.last_name.charAt(0)}
                         </span>
                       </div>
                       <div className="space-y-1">
                         <p className="font-semibold">{lead.first_name} {lead.last_name}</p>
                         <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                           <span className="flex items-center gap-1">
                             <Mail className="h-3 w-3" />
                             {lead.email}
                           </span>
                           {lead.phone && (
                             <span className="flex items-center gap-1">
                               <Phone className="h-3 w-3" />
                               {lead.phone}
                             </span>
                           )}
                         </div>
                         {lead.preferred_location && lead.preferred_location.length > 0 && (
                           <div className="flex items-center gap-1 text-sm text-muted-foreground">
                             <MapPin className="h-3 w-3" />
                             {lead.preferred_location.map(l => l.charAt(0).toUpperCase() + l.slice(1)).join(", ")}
                           </div>
                         )}
                       </div>
                     </div>
                     
                     <div className="flex flex-col items-end gap-2">
                       <div className="flex items-center gap-2">
                         <Badge variant={lead.status === "pending" ? "secondary" : "default"}>
                           {lead.status}
                         </Badge>
                         {lead.timeframe && (
                           <Badge variant="outline">{lead.timeframe}</Badge>
                         )}
                       </div>
                       <span className="text-xs text-muted-foreground flex items-center gap-1">
                         <Calendar className="h-3 w-3" />
                         {format(new Date(lead.created_at), "MMM d, yyyy")}
                       </span>
                       <div className="flex gap-2">
                         <Button size="sm" variant="outline" asChild>
                           <a href={`mailto:${lead.email}`}>
                             <Mail className="h-3 w-3 mr-1" />
                             Email
                           </a>
                         </Button>
                         {lead.phone && (
                           <Button size="sm" variant="outline" asChild>
                             <a href={`tel:${lead.phone}`}>
                               <Phone className="h-3 w-3 mr-1" />
                               Call
                             </a>
                           </Button>
                         )}
                       </div>
                     </div>
                   </div>
                   
                   {lead.care_needs && lead.care_needs.length > 0 && (
                     <div className="mt-3 pt-3 border-t">
                       <p className="text-sm text-muted-foreground mb-2">Care Needs:</p>
                       <div className="flex flex-wrap gap-1">
                         {lead.care_needs.map((need, idx) => (
                           <Badge key={idx} variant="outline" className="text-xs">
                             {need}
                           </Badge>
                         ))}
                       </div>
                     </div>
                   )}
                 </CardContent>
               </Card>
             ))}
           </div>
         </div>
       ) : leadCount === 0 ? (
         <Card>
           <CardContent className="py-12 text-center">
             <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
             <h3 className="text-lg font-medium mb-2">No Leads Yet</h3>
             <p className="text-muted-foreground max-w-md mx-auto">
               When families express interest in care homes in {nursingHome.county}, 
               their inquiries will appear here.
             </p>
           </CardContent>
         </Card>
       ) : null}
     </div>
   );
 }